import { useShowSpinner } from 'hooks';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router';
import { useBrandStore } from 'stores';
import type { WorkflowExecutionRow } from '../BrandWorkflows.types';
import useBrandWorkflowsApi from '../useBrandWorkflowsApi';
import { pickSchedulerByType } from '../workflowFormatters';
import WorkflowRunHistoryView from './WorkflowRunHistory.View';

/* Drill-in from a Latest-tab row: the last runs of one workflow type, each row expanding
   into the shared WorkflowTasksDetails */
const WorkflowRunHistory = () => {
  const { workflowType = '' } = useParams();
  const { brand } = useBrandStore();
  const api = useBrandWorkflowsApi();
  const apiRef = useRef(api);
  apiRef.current = api;

  const [rows, setRows] = useState<WorkflowExecutionRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useShowSpinner({ show: loading });

  const loadRuns = useCallback(async () => {
    setLoading(true);
    try {
      const [historyResult, schedulersResult] = await Promise.all([
        apiRef.current.getExecutionHistory(workflowType),
        apiRef.current.getSchedulers()
      ]);
      if (historyResult.data) {
        // A schedulers failure only degrades the Schedule column, never the page
        const schedulerByType = pickSchedulerByType(schedulersResult.data ?? []);
        setRows(
          historyResult.data.map(run => ({
            ...run,
            scheduler: schedulerByType.get(run.workflowType) ?? null
          }))
        );
        setError(null);
      } else {
        setRows([]);
        setError(historyResult.error);
      }
    } catch (e) {
      setRows([]);
      setError(e instanceof Error ? e.message : 'Failed to load run history');
    } finally {
      setLoading(false);
    }
  }, [workflowType]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  return (
    <WorkflowRunHistoryView
      workflowType={workflowType}
      brandName={brand.name}
      workflowsPath={`/brand/${brand.brandId}/workflows`}
      rows={rows}
      loading={loading}
      error={error}
    />
  );
};

export default WorkflowRunHistory;
